import React, { useEffect, useRef, useState } from 'react';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import Loading from '../../../components/common/Loading/Loading';
import Pagination from '../../../components/common/Pagination/Pagination';
import SearchFilterBar from '../../../components/common/SearchFilterBar/SearchFilterBar';
import { TrainingService } from '../../../services';
import type { PaginationInfo } from '../../../types';
import type { TrainingItem } from '../../../types/training';
import styles from './TrainingCatalogSearchModal.module.css';

const PAGE_SIZE = 10;

const EMPTY_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: PAGE_SIZE,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

interface TrainingCatalogSearchModalProps {
  open: boolean;
  onSelect: (training: TrainingItem) => void;
  onCancel: () => void;
}

const TrainingCatalogSearchModal: React.FC<TrainingCatalogSearchModalProps> = ({
  open,
  onSelect,
  onCancel,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [trainings, setTrainings] = useState<TrainingItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setPage(1);
      setTrainings([]);
      setPagination(EMPTY_PAGINATION);
      setError('');
      setLoading(true);
      return;
    }

    setLoading(true);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const currentRequestId = ++requestIdRef.current;
    const debounceHandle = window.setTimeout(() => {
      const loadTrainings = async () => {
        try {
          setLoading(true);
          const response = await TrainingService.listPaginated(page, PAGE_SIZE, {
            searchTerm: searchTerm.trim(),
          });

          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          setTrainings(response.data);
          setPagination(response.pagination);
          setError('');
        } catch (loadError) {
          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          setTrainings([]);
          setPagination((current) => ({
            ...current,
            page,
            pageSize: PAGE_SIZE,
            totalRecords: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          }));
          setError(loadError instanceof Error ? loadError.message : 'Não foi possível carregar os treinos.');
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      };

      void loadTrainings();
    }, 300);

    return () => window.clearTimeout(debounceHandle);
  }, [open, page, searchTerm]);

  return (
    <div className={styles.stack}>
      <ConfirmModal
        open={open}
        title="Buscar treino"
        message="Selecione um treino do catálogo para preencher o próximo treino."
        confirmLabel="Fechar"
        hideCancel
        variant="info"
        width={560}
        onConfirm={onCancel}
        onCancel={onCancel}
      >
        <div className={styles.body}>
          <SearchFilterBar
            searchValue={searchTerm}
            onSearchChange={(value) => {
              setSearchTerm(value);
              setPage(1);
            }}
            searchPlaceholder="Pesquisar"
            searchWrapperClassName={styles['search-wrap']}
            inputClassName={styles['search-input']}
          />

          {loading ? (
            <Loading inline />
          ) : error ? (
            <EmptyState message={error} showImage={false} />
          ) : trainings.length === 0 ? (
            <EmptyState message="Nenhum treino encontrado." showImage={false} />
          ) : (
            <ul className={styles.list}>
              {trainings.map((training) => (
                <li key={training.id}>
                  <button
                    type="button"
                    className={styles.item}
                    onClick={() => onSelect(training)}
                  >
                    <span className={styles['item-name']}>{training.name}</span>
                    {training.code ? (
                      <span className={styles['item-code']}>{training.code}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {pagination.totalPages > 1 && (
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={setPage}
            />
          )}
        </div>
      </ConfirmModal>
    </div>
  );
};

export default TrainingCatalogSearchModal;
